import { useEffect, useRef, useCallback } from "react";
import { API_URL } from "../config";
import { normalizeLoad } from "../domain/load";

/**
 * Hook for managing SSE connections for multiple tabs simultaneously
 * Each tab with a searchId gets its own SSE connection and heartbeat
 * 
 * @param {Array} tabs - All tabs from SearchTabsContext
 * @param {function} onLoadsReceived - Callback when loads are received (tabId, loads, source, status)
 * @param {function} onError - Optional callback for errors (tabId, error)
 */
export const useMultiTabSSE = (tabs, onLoadsReceived, onError) => {
  // Map of searchId -> connection info (EventSource, intervals, etc.)
  const connectionsRef = useRef(new Map());

  // Send heartbeat for a specific searchId
  const sendHeartbeat = useCallback(async (searchId) => {
    if (!searchId) return;

    try {
      const response = await fetch(`${API_URL}/loads/search/${searchId}/heartbeat`, {
        method: "POST",
      });

      if (!response.ok) {
        if (response.status === 410) {
          // Search expired - will be handled by event listener
          console.warn(`[SSE] Search ${searchId} expired`);
        } else if (response.status === 404) {
          // Search not found - clean up connection
          const conn = connectionsRef.current.get(searchId);
          if (conn) {
            if (conn.eventSource) conn.eventSource.close();
            if (conn.heartbeatInterval) clearInterval(conn.heartbeatInterval);
            connectionsRef.current.delete(searchId);
          }
        }
        return;
      }

      const data = await response.json();
      
      // Warn if search is about to expire (less than 15 minutes)
      if (data.time_remaining_seconds < 900 && data.time_remaining_seconds > 0) {
        console.warn(
          `[SSE] Search ${searchId} expires in ${Math.round(data.time_remaining_seconds / 60)} minutes`
        );
      }
    } catch (err) {
      // Don't log heartbeat errors - they're expected if connection is temporarily lost
      console.debug(`[SSE] Heartbeat failed for ${searchId}:`, err.message);
    }
  }, []);

  // Create SSE connection for a tab
  const createConnection = useCallback((tab) => {
    const { id: tabId, searchId } = tab;
    
    if (!searchId) return;
    
    // Skip if connection already exists
    if (connectionsRef.current.has(searchId)) {
      return;
    }

    console.log(`[SSE] Creating connection for tab ${tabId} with searchId ${searchId}`);

    // Create EventSource connection
    const eventSource = new EventSource(`${API_URL}/loads/search/stream/${searchId}`);
    
    // Connection info
    const conn = {
      tabId,
      searchId,
      eventSource,
      heartbeatInterval: null,
    };

    // Handle connection open
    eventSource.onopen = () => {
      console.log(`[SSE] Connected to search ${searchId} for tab ${tabId}`);
    };

    // Handle updates
    eventSource.addEventListener("update", (event) => {
      try {
        const data = JSON.parse(event.data);

        // Call onLoadsReceived even with empty loads to clear ssePending flag
        if (data.loads && onLoadsReceived) {
          const normalizedLoads = data.loads.length > 0 ? data.loads.map(normalizeLoad) : [];
          // Pass tabId so handler knows which tab to update
          onLoadsReceived(tabId, normalizedLoads, data.source, data.status);
        }
      } catch (err) {
        console.error(`[SSE] Error parsing update for tab ${tabId}:`, err);
      }
    });

    // Handle expiration
    eventSource.addEventListener("expired", (event) => {
      console.warn(`[SSE] Search ${searchId} expired for tab ${tabId}`);
      if (onError) {
        onError(tabId, "Search expired (max lifetime reached)");
      }
      eventSource.close();
      if (conn.heartbeatInterval) {
        clearInterval(conn.heartbeatInterval);
      }
      connectionsRef.current.delete(searchId);
    });

    // Handle errors
    eventSource.onerror = (err) => {
      if (eventSource.readyState === EventSource.CLOSED) {
        console.warn(`[SSE] Connection closed for search ${searchId} (tab ${tabId})`);
        if (onError) {
          onError(tabId, "Connection closed. The search session may have expired.");
        }
        if (conn.heartbeatInterval) {
          clearInterval(conn.heartbeatInterval);
        }
        connectionsRef.current.delete(searchId);
      }
    };

    // Start heartbeat (every 30 seconds)
    conn.heartbeatInterval = setInterval(() => {
      sendHeartbeat(searchId);
    }, 30000);

    // Send initial heartbeat
    sendHeartbeat(searchId);

    // Store connection
    connectionsRef.current.set(searchId, conn);
  }, [onLoadsReceived, onError, sendHeartbeat]);

  // Clean up connection for a tab
  const cleanupConnection = useCallback((searchId) => {
    const conn = connectionsRef.current.get(searchId);
    if (!conn) return;

    console.log(`[SSE] Cleaning up connection for searchId ${searchId} (tab ${conn.tabId})`);

    if (conn.heartbeatInterval) {
      clearInterval(conn.heartbeatInterval);
    }
    if (conn.eventSource) {
      conn.eventSource.close();
    }
    connectionsRef.current.delete(searchId);
  }, []);

  // Main effect: manage connections based on tabs
  useEffect(() => {
    // Get all tabs with searchId
    const tabsWithSearch = tabs.filter((tab) => tab.searchId);

    // Create connections for new tabs with searchId
    tabsWithSearch.forEach((tab) => {
      const existingConn = connectionsRef.current.get(tab.searchId);
      
      // If no connection exists, create one
      if (!existingConn) {
        createConnection(tab);
      } 
      // If connection exists but is for a different tab, something went wrong
      // This shouldn't happen, but log it if it does
      else if (existingConn.tabId !== tab.id) {
        console.warn(
          `[SSE] SearchId ${tab.searchId} is associated with tab ${existingConn.tabId}, but tab ${tab.id} also has it. Cleaning up old connection.`
        );
        cleanupConnection(tab.searchId);
        createConnection(tab);
      }
      // If connection exists and matches, do nothing (connection is already active)
    });

    // Clean up connections for tabs that no longer have searchId or were removed
    const activeSearchIds = new Set(tabsWithSearch.map((tab) => tab.searchId));
    
    connectionsRef.current.forEach((conn, searchId) => {
      // If searchId is no longer in any tab (tab was removed or searchId cleared), clean up
      if (!activeSearchIds.has(searchId)) {
        console.log(`[SSE] Tab ${conn.tabId} was removed or searchId cleared - cleaning up connection for searchId ${searchId}`);
        cleanupConnection(searchId);
      }
    });
  }, [tabs, createConnection, cleanupConnection]);

  // Cleanup on unmount
  useEffect(() => {
    const connections = connectionsRef.current;
    return () => {
      console.log("[SSE] Cleaning up all connections");
      connections.forEach((conn, searchId) => {
        cleanupConnection(searchId);
      });
    };
  }, [cleanupConnection]);

  // Return connection statuses (optional - for debugging/monitoring)
  return {
    activeConnections: Array.from(connectionsRef.current.keys()),
    getConnectionStatus: (searchId) => {
      const conn = connectionsRef.current.get(searchId);
      if (!conn) return null;
      return {
        tabId: conn.tabId,
        searchId: conn.searchId,
        readyState: conn.eventSource?.readyState,
      };
    },
  };
};

